import React, { useMemo, useState } from 'react';

import { useTranslation } from '@modernloop/i18n';
import { DiamondFilled, ReverseShadowIcon, ShadowIcon } from '@modernloop/shared/icons';
import { Box, Divider, Stack } from '@mui/material';

import {
  AttributeNameType,
  EmployeeFilterFragment,
  TrainingStatus,
  useEmployeeAttributeNamesQuery,
} from 'src/generated/mloop-graphql';

import {
  JobStageInterviewSeat,
  TRAINEE_REQUIREMENT,
  TRAINEE_SCHEDULING_STRATEGY,
  TraineeSchedulingStrategy,
} from 'src/store/slices/job-stage-interview-seat';

import { memberMatchesAttributeMap, hasSelectedAttributes, resolveInTrainingAttributeMap } from '../memberFiltering';

import ModuleFilterDropdownButton from './ModuleFilterDropdownButton';
import ModuleFilterRow from './ModuleFilterRow';
import { SOURCE_MODE, SourceMode } from './seatSettingsTypes';
import {
  addAttribute,
  getInTrainingSourceMode,
  isReverseShadowRequirementEnabled,
  isShadowRequirementEnabled,
  removeAttribute,
  traineeRequirementFromToggles,
} from './seatSettingsUtils';
import TraineeRequirementRow from './TraineeRequirementRow';

type Props = {
  interviewModuleMembers: EmployeeFilterFragment[];
  seat: JobStageInterviewSeat;
  onUpdated: (seat: JobStageInterviewSeat) => void;
};

/**
 * Compact seat settings editor with per-role filter rows and trainee scheduling controls.
 */
const SeatSettings = ({ interviewModuleMembers, seat, onUpdated }: Props): JSX.Element => {
  const { t } = useTranslation();

  const requirementType = seat.traineeRequirement ?? TRAINEE_REQUIREMENT.ANY;
  const reverseShadowEnabled = isReverseShadowRequirementEnabled(requirementType);
  const shadowEnabled = isShadowRequirementEnabled(requirementType);
  const traineeRowsEnabled = reverseShadowEnabled || shadowEnabled;
  const schedulingStrategy = seat.traineeSchedulingStrategy ?? TRAINEE_SCHEDULING_STRATEGY.IS_AVAILABLE;
  const { shadowAttributeMap } = seat;
  const { reverseShadowAttributeMap } = seat;

  const trainedHasPeople = (seat.selectedTrainedInterviewerIds?.length ?? 0) > 0;
  const shadowHasPeople = (seat.selectedShadowInterviewerIds?.length ?? 0) > 0;
  const reverseShadowHasPeople = (seat.selectedReverseShadowInterviewerIds?.length ?? 0) > 0;

  const [trainedSource, setTrainedSource] = useState<SourceMode>(
    hasSelectedAttributes(seat.attributeMap) || !trainedHasPeople ? SOURCE_MODE.ATTRIBUTES : SOURCE_MODE.PEOPLE
  );
  const [shadowSource, setShadowSource] = useState<SourceMode>(
    getInTrainingSourceMode({
      attributeMap: shadowAttributeMap,
      hasPeople: shadowHasPeople,
    })
  );
  const [reverseShadowSource, setReverseShadowSource] = useState<SourceMode>(
    getInTrainingSourceMode({
      attributeMap: reverseShadowAttributeMap,
      hasPeople: reverseShadowHasPeople,
    })
  );

  const {
    loading: attributesLoading,
    error: attributesError,
    data: attributesData,
  } = useEmployeeAttributeNamesQuery({
    variables: { input: { types: Object.values(AttributeNameType) } },
    fetchPolicy: 'network-only',
  });

  const attributeData = attributesData?.employeeAttributeNames?.items ?? [];

  const allTrainedMembers = useMemo(
    () => interviewModuleMembers.filter((member) => member.status === TrainingStatus.Trained),
    [interviewModuleMembers]
  );
  const allShadowMembers = useMemo(
    () => interviewModuleMembers.filter((member) => member.status === TrainingStatus.Shadow),
    [interviewModuleMembers]
  );
  const allReverseShadowMembers = useMemo(
    () => interviewModuleMembers.filter((member) => member.status === TrainingStatus.ReverseShadow),
    [interviewModuleMembers]
  );

  const selectedTrainedCount = seat.selectedTrainedInterviewerIds?.length ?? 0;
  const selectedShadowCount = seat.selectedShadowInterviewerIds?.length ?? 0;
  const selectedReverseShadowCount = seat.selectedReverseShadowInterviewerIds?.length ?? 0;

  const effectiveShadowAttributeMap = useMemo(
    () =>
      resolveInTrainingAttributeMap({
        role: 'SHADOW',
        attributeMap: seat.attributeMap,
        shadowAttributeMap,
        reverseShadowAttributeMap,
      }),
    [reverseShadowAttributeMap, seat.attributeMap, shadowAttributeMap]
  );
  const effectiveReverseShadowAttributeMap = useMemo(
    () =>
      resolveInTrainingAttributeMap({
        role: 'REVERSE_SHADOW',
        attributeMap: seat.attributeMap,
        shadowAttributeMap,
        reverseShadowAttributeMap,
      }),
    [reverseShadowAttributeMap, seat.attributeMap, shadowAttributeMap]
  );

  const matchingTrainedCount = useMemo(() => {
    if (trainedSource === SOURCE_MODE.PEOPLE) {
      return selectedTrainedCount > 0 ? selectedTrainedCount : allTrainedMembers.length;
    }

    return allTrainedMembers.filter((member) => memberMatchesAttributeMap(member, seat.attributeMap)).length;
  }, [allTrainedMembers, seat.attributeMap, selectedTrainedCount, trainedSource]);

  const matchingShadowCount = useMemo(() => {
    if (!shadowEnabled) {
      return 0;
    }
    if (shadowSource === SOURCE_MODE.PEOPLE) {
      return selectedShadowCount > 0 ? selectedShadowCount : allShadowMembers.length;
    }

    return allShadowMembers.filter((member) => memberMatchesAttributeMap(member, effectiveShadowAttributeMap)).length;
  }, [allShadowMembers, effectiveShadowAttributeMap, selectedShadowCount, shadowEnabled, shadowSource]);

  const matchingReverseShadowCount = useMemo(() => {
    if (!reverseShadowEnabled) {
      return 0;
    }
    if (reverseShadowSource === SOURCE_MODE.PEOPLE) {
      return selectedReverseShadowCount > 0 ? selectedReverseShadowCount : allReverseShadowMembers.length;
    }

    return allReverseShadowMembers.filter((member) =>
      memberMatchesAttributeMap(member, effectiveReverseShadowAttributeMap)
    ).length;
  }, [
    allReverseShadowMembers,
    effectiveReverseShadowAttributeMap,
    reverseShadowEnabled,
    reverseShadowSource,
    selectedReverseShadowCount,
  ]);

  const handleTraineeStrategyChange = (strategy: TraineeSchedulingStrategy) => {
    onUpdated({ ...seat, traineeSchedulingStrategy: strategy });
  };

  const handleTrainedSourceChange = (mode: SourceMode) => {
    setTrainedSource(mode);
    if (mode === SOURCE_MODE.PEOPLE) {
      onUpdated({ ...seat, attributeMap: undefined });
      return;
    }
    onUpdated({ ...seat, selectedTrainedInterviewerIds: undefined });
  };

  const handleInTrainingSourceChange = (role: 'SHADOW' | 'REVERSE_SHADOW', mode: SourceMode) => {
    if (role === 'SHADOW') {
      setShadowSource(mode);
    } else {
      setReverseShadowSource(mode);
    }

    if (mode === SOURCE_MODE.PEOPLE) {
      onUpdated({
        ...seat,
        shadowAttributeMap: role === 'SHADOW' ? undefined : seat.shadowAttributeMap,
        reverseShadowAttributeMap: role === 'REVERSE_SHADOW' ? undefined : seat.reverseShadowAttributeMap,
      });
      return;
    }

    onUpdated({
      ...seat,
      selectedShadowInterviewerIds: role === 'SHADOW' ? undefined : seat.selectedShadowInterviewerIds,
      selectedReverseShadowInterviewerIds:
        role === 'REVERSE_SHADOW' ? undefined : seat.selectedReverseShadowInterviewerIds,
    });
  };

  const handleTrainedAttributeUpdate = (nameId: string, valueId: string, selected: boolean) => {
    onUpdated({
      ...seat,
      attributeMap: selected
        ? addAttribute(seat.attributeMap, nameId, valueId)
        : removeAttribute(seat.attributeMap, nameId, valueId),
      selectedTrainedInterviewerIds: undefined,
    });
  };

  const handleInTrainingAttributeUpdate = (
    role: 'SHADOW' | 'REVERSE_SHADOW',
    nameId: string,
    valueId: string,
    selected: boolean
  ) => {
    let nextShadowAttributeMap = seat.shadowAttributeMap;
    if (role === 'SHADOW') {
      nextShadowAttributeMap = selected
        ? addAttribute(shadowAttributeMap, nameId, valueId)
        : removeAttribute(shadowAttributeMap, nameId, valueId);
    }

    let nextReverseShadowAttributeMap = seat.reverseShadowAttributeMap;
    if (role === 'REVERSE_SHADOW') {
      nextReverseShadowAttributeMap = selected
        ? addAttribute(reverseShadowAttributeMap, nameId, valueId)
        : removeAttribute(reverseShadowAttributeMap, nameId, valueId);
    }

    onUpdated({
      ...seat,
      shadowAttributeMap: nextShadowAttributeMap,
      reverseShadowAttributeMap: nextReverseShadowAttributeMap,
    });
  };

  const clearReverseShadowFilters = (nextSeat: JobStageInterviewSeat): JobStageInterviewSeat => ({
    ...nextSeat,
    reverseShadowAttributeMap: undefined,
    selectedReverseShadowInterviewerIds: undefined,
  });

  const clearShadowFilters = (nextSeat: JobStageInterviewSeat): JobStageInterviewSeat => ({
    ...nextSeat,
    shadowAttributeMap: undefined,
    selectedShadowInterviewerIds: undefined,
  });

  const handleReverseShadowToggle = (enabled: boolean) => {
    const nextRequirement = traineeRequirementFromToggles({
      reverseShadowEnabled: enabled,
      shadowEnabled,
    });
    let updatedSeat: JobStageInterviewSeat = {
      ...seat,
      traineeRequirement: nextRequirement,
      traineeSchedulingStrategy: nextRequirement === TRAINEE_REQUIREMENT.NONE ? undefined : schedulingStrategy,
    };

    if (!enabled) {
      updatedSeat = clearReverseShadowFilters(updatedSeat);
    } else if (!seat.traineeSchedulingStrategy) {
      updatedSeat.traineeSchedulingStrategy = TRAINEE_SCHEDULING_STRATEGY.IS_AVAILABLE;
    }

    onUpdated(updatedSeat);
  };

  const handleShadowToggle = (enabled: boolean) => {
    const nextRequirement = traineeRequirementFromToggles({
      reverseShadowEnabled,
      shadowEnabled: enabled,
    });
    let updatedSeat: JobStageInterviewSeat = {
      ...seat,
      traineeRequirement: nextRequirement,
      traineeSchedulingStrategy: nextRequirement === TRAINEE_REQUIREMENT.NONE ? undefined : schedulingStrategy,
    };

    if (!enabled) {
      updatedSeat = clearShadowFilters(updatedSeat);
    } else if (!seat.traineeSchedulingStrategy) {
      updatedSeat.traineeSchedulingStrategy = TRAINEE_SCHEDULING_STRATEGY.IS_AVAILABLE;
    }

    onUpdated(updatedSeat);
  };

  return (
    <Box data-testid="seat-settings">
      <Divider sx={{ marginBottom: 1.5 }} />
      <Stack spacing={1.25}>
        <ModuleFilterRow
          accent="success"
          count={matchingTrainedCount}
          icon={<DiamondFilled color="inherit" />}
          label={t('hiring-dashboard-filter:filter.tabTrainedAttributes')}
          testId="module-filter-row-trained"
          filterButton={
            <ModuleFilterDropdownButton
              role="TRAINED"
              seat={seat}
              interviewModuleMembers={allTrainedMembers}
              attributeData={attributeData}
              attributesLoading={attributesLoading}
              attributesError={attributesError}
              sourceMode={trainedSource}
              onSourceModeChange={handleTrainedSourceChange}
              attributeMap={seat.attributeMap}
              selectedPeopleCount={selectedTrainedCount}
              onAttributeUpdate={handleTrainedAttributeUpdate}
              onUpdated={(updated) => onUpdated({ ...updated, attributeMap: undefined })}
            />
          }
        />

        <ModuleFilterRow
          accent="primary"
          count={matchingReverseShadowCount}
          icon={<ReverseShadowIcon color="inherit" />}
          label={t('hiring-dashboard-schedule:schedule.suggestedSchedule.reverseShadow')}
          showToggle
          toggleEnabled={reverseShadowEnabled}
          onToggle={handleReverseShadowToggle}
          testId="module-filter-row-reverse-shadow"
          filterButton={
            <ModuleFilterDropdownButton
              key={`reverse-shadow-${reverseShadowEnabled}`}
              role="REVERSE_SHADOW"
              seat={seat}
              interviewModuleMembers={allReverseShadowMembers}
              attributeData={attributeData}
              attributesLoading={attributesLoading}
              attributesError={attributesError}
              sourceMode={reverseShadowSource}
              onSourceModeChange={(mode) => handleInTrainingSourceChange('REVERSE_SHADOW', mode)}
              attributeMap={reverseShadowAttributeMap}
              selectedPeopleCount={selectedReverseShadowCount}
              disabled={!reverseShadowEnabled}
              onAttributeUpdate={(nameId, valueId, selected) =>
                handleInTrainingAttributeUpdate('REVERSE_SHADOW', nameId, valueId, selected)
              }
              onUpdated={(updated) =>
                onUpdated({
                  ...updated,
                  reverseShadowAttributeMap: undefined,
                })
              }
            />
          }
        />

        <ModuleFilterRow
          accent="info"
          count={matchingShadowCount}
          icon={<ShadowIcon color="inherit" />}
          label={t('hiring-dashboard-schedule:schedule.suggestedSchedule.shadow')}
          showToggle
          toggleEnabled={shadowEnabled}
          onToggle={handleShadowToggle}
          testId="module-filter-row-shadow"
          filterButton={
            <ModuleFilterDropdownButton
              key={`shadow-${shadowEnabled}`}
              role="SHADOW"
              seat={seat}
              interviewModuleMembers={allShadowMembers}
              attributeData={attributeData}
              attributesLoading={attributesLoading}
              attributesError={attributesError}
              sourceMode={shadowSource}
              onSourceModeChange={(mode) => handleInTrainingSourceChange('SHADOW', mode)}
              attributeMap={shadowAttributeMap}
              selectedPeopleCount={selectedShadowCount}
              disabled={!shadowEnabled}
              onAttributeUpdate={(nameId, valueId, selected) =>
                handleInTrainingAttributeUpdate('SHADOW', nameId, valueId, selected)
              }
              onUpdated={(updated) =>
                onUpdated({
                  ...updated,
                  shadowAttributeMap: undefined,
                })
              }
            />
          }
        />

        {traineeRowsEnabled && (
          <TraineeRequirementRow
            schedulingStrategy={schedulingStrategy}
            onStrategyChange={handleTraineeStrategyChange}
          />
        )}
      </Stack>
    </Box>
  );
};

export default SeatSettings;
